import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType, AlignmentType, HeadingLevel, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import logoImage from '../assets/logo.png';

const COLORS = {
    gering: '10B981',
    normal: '22C55E',
    hoch: 'F59E0B',
    sehr_hoch: 'EF4444',
    primary: 'D4AF37',
};

const LABELS = {
    gering: 'Gering',
    normal: 'Normal',
    hoch: 'Hoch',
    sehr_hoch: 'Sehr Hoch',
};

const STATUS_LABELS = {
    entwurf: 'Entwurf',
    in_bearbeitung: 'In Bearbeitung',
    abgeschlossen: 'Abgeschlossen',
};

const createCoverPage = async (mandantName, currentDate) => {
    const paragraphs = [];

    try {
        const response = await fetch(logoImage);
        const arrayBuffer = await response.arrayBuffer();

        paragraphs.push(
            new Paragraph({
                children: [
                    new ImageRun({
                        data: arrayBuffer,
                        transformation: {
                            width: 200,
                            height: 200,
                        },
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 },
            })
        );
    } catch (error) {
        console.error('Logo-Fehler:', error);
        paragraphs.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'REGENTRA',
                        size: 48,
                        bold: true,
                        color: 'D4AF37',
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 },
            })
        );
    }

    paragraphs.push(
        new Paragraph({
            text: 'Schutzbedarfsanalyse',
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            text: 'gemäß BSI IT-Grundschutz',
            alignment: AlignmentType.CENTER,
            spacing: { after: 1200 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: '─────────────────────────────────',
                    color: 'E5E7EB',
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Mandant: ',
                    bold: true,
                    size: 24,
                }),
                new TextRun({
                    text: mandantName,
                    size: 24,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Erstellt am: ',
                    bold: true,
                    size: 24,
                }),
                new TextRun({
                    text: currentDate,
                    size: 24,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Vertraulich',
                    italic: true,
                    size: 20,
                    color: '6B7280',
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 800, after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            text: '',
            pageBreakBefore: true,
        })
    );

    return paragraphs;
};

const createExecutiveSummary = (prozesse, analysen, anwendungen) => {
    const gesamtProzesse = prozesse.length;
    const gesamtAnwendungen = anwendungen.length;
    const abgeschlosseneAnalysen = analysen.filter(a => a.status === 'abgeschlossen').length;

    return [
        new Paragraph({
            text: 'Management Summary',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),

        new Paragraph({
            text: 'Überblick',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 200 },
        }),

        new Paragraph({
            text: `Diese Schutzbedarfsanalyse wurde gemäß den Vorgaben des BSI IT-Grundschutz erstellt und umfasst ${gesamtProzesse} Geschäftsprozesse, ${gesamtAnwendungen} IT-Anwendungen sowie insgesamt ${analysen.length} Schutzbedarfsbewertungen.`,
            spacing: { after: 400 },
        }),

        createSummaryTable(gesamtProzesse, gesamtAnwendungen, abgeschlosseneAnalysen),

        new Paragraph({
            text: '',
            pageBreakBefore: true,
        }),
    ];
};

const createSummaryTable = (prozesse, anwendungen, analysen) => {
    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Kennzahl', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: 'Wert', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Analysierte Prozesse')] }),
                    new TableCell({ children: [new Paragraph(prozesse.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Erfasste Anwendungen')] }),
                    new TableCell({ children: [new Paragraph(anwendungen.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Abgeschlossene Analysen')] }),
                    new TableCell({ children: [new Paragraph(analysen.toString())] }),
                ],
            }),
        ],
    });
};

const createProzesseSection = (prozesse, analysen) => {
    const sections = [
        new Paragraph({
            text: 'Geschäftsprozesse',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),
    ];

    prozesse.forEach((prozess, index) => {
        const prozessAnalysen = analysen.filter(a => a.prozess_id === prozess.prozess_id);
        const neuesteAnalyse = prozessAnalysen.sort((a, b) =>
            new Date(b.analyse_datum) - new Date(a.analyse_datum)
        )[0];

        sections.push(
            new Paragraph({
                text: `${index + 1}. ${prozess.prozess_name}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 200 },
            })
        );

        if (prozess.prozess_beschreibung) {
            sections.push(
                new Paragraph({
                    text: prozess.prozess_beschreibung,
                    spacing: { after: 200 },
                })
            );
        }

        sections.push(createProzessMetaTable(prozess, neuesteAnalyse));

        if (neuesteAnalyse) {
            sections.push(
                new Paragraph({
                    text: 'Schutzziele',
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 },
                })
            );
            sections.push(createSchutzzieleTable(neuesteAnalyse));
        }

        sections.push(
            new Paragraph({
                text: '',
                spacing: { after: 400 },
            })
        );
    });

    sections.push(
        new Paragraph({
            text: '',
            pageBreakBefore: true,
        })
    );

    return sections;
};

const createAnwendungenSection = (anwendungen, prozesse) => {
    const sections = [
        new Paragraph({
            text: 'IT-Anwendungen',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),
    ];

    if (anwendungen.length === 0) {
        sections.push(
            new Paragraph({
                text: 'Keine Anwendungen erfasst.',
                spacing: { after: 200 },
            })
        );
        return sections;
    }

    anwendungen.forEach((anwendung, index) => {
        const prozess = prozesse.find(p => p.prozess_id === anwendung.prozess_id);

        sections.push(
            new Paragraph({
                text: `${index + 1}. ${anwendung.anwendung_name}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 200 },
            })
        );

        if (anwendung.anwendung_beschreibung) {
            sections.push(
                new Paragraph({
                    text: anwendung.anwendung_beschreibung,
                    spacing: { after: 200 },
                })
            );
        }

        sections.push(createAnwendungMetaTable(anwendung, prozess));

        sections.push(
            new Paragraph({
                text: '',
                spacing: { after: 400 },
            })
        );
    });

    sections.push(
        new Paragraph({
            text: '',
            pageBreakBefore: true,
        })
    );

    return sections;
};

const createAnwendungMetaTable = (anwendung, prozess) => {
    const rows = [
        new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ text: 'Kategorie', bold: true })],
                    width: { size: 30, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph(anwendung.anwendung_kategorie || '-')],
                    width: { size: 70, type: WidthType.PERCENTAGE },
                }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Verantwortlich', bold: true })] }),
                new TableCell({ children: [new Paragraph(anwendung.anwendung_verantwortlicher || '-')] }),
            ],
        }),
    ];

    if (prozess) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Zugeordneter Prozess', bold: true })] }),
                    new TableCell({ children: [new Paragraph(prozess.prozess_name)] }),
                ],
            })
        );
    }

    if (anwendung.gesamtschutzbedarf) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Gesamtschutzbedarf', bold: true })] }),
                    new TableCell({
                        children: [new Paragraph({
                            text: LABELS[anwendung.gesamtschutzbedarf] || anwendung.gesamtschutzbedarf,
                            bold: true,
                        })],
                        shading: { fill: COLORS[anwendung.gesamtschutzbedarf] || 'FFFFFF' },
                    }),
                ],
            })
        );
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
        margins: {
            top: 100,
            bottom: 100,
            left: 100,
            right: 100,
        },
    });
};

const createProzessMetaTable = (prozess, analyse) => {
    const rows = [
        new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ text: 'Kategorie', bold: true })],
                    width: { size: 30, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph(prozess.prozess_kategorie || '-')],
                    width: { size: 70, type: WidthType.PERCENTAGE },
                }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Verantwortlich', bold: true })] }),
                new TableCell({ children: [new Paragraph(prozess.prozess_verantwortlicher || '-')] }),
            ],
        }),
    ];

    if (analyse) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Gesamtschutzbedarf', bold: true })] }),
                    new TableCell({
                        children: [new Paragraph({
                            text: LABELS[analyse.gesamtschutzbedarf] || '-',
                            bold: true,
                        })],
                        shading: { fill: COLORS[analyse.gesamtschutzbedarf] || 'FFFFFF' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Status', bold: true })] }),
                    new TableCell({
                        children: [new Paragraph(STATUS_LABELS[analyse.status] || analyse.status)],
                    }),
                ],
            })
        );
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
        margins: {
            top: 100,
            bottom: 100,
            left: 100,
            right: 100,
        },
    });
};

const createSchutzzieleTable = (analyse) => {
    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Schutzziel', bold: true })],
                        shading: { fill: 'F3F4F6' },
                        width: { size: 20, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: 'Einstufung', bold: true })],
                        shading: { fill: 'F3F4F6' },
                        width: { size: 20, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: 'Begründung', bold: true })],
                        shading: { fill: 'F3F4F6' },
                        width: { size: 60, type: WidthType.PERCENTAGE },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Vertraulichkeit')] }),
                    new TableCell({
                        children: [new Paragraph(LABELS[analyse.vertraulichkeit] || analyse.vertraulichkeit)],
                        shading: { fill: COLORS[analyse.vertraulichkeit] || 'FFFFFF' },
                    }),
                    new TableCell({ children: [new Paragraph(analyse.vertraulichkeit_begruendung || '-')] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Integrität')] }),
                    new TableCell({
                        children: [new Paragraph(LABELS[analyse.integritaet] || analyse.integritaet)],
                        shading: { fill: COLORS[analyse.integritaet] || 'FFFFFF' },
                    }),
                    new TableCell({ children: [new Paragraph(analyse.integritaet_begruendung || '-')] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Verfügbarkeit')] }),
                    new TableCell({
                        children: [new Paragraph(LABELS[analyse.verfuegbarkeit] || analyse.verfuegbarkeit)],
                        shading: { fill: COLORS[analyse.verfuegbarkeit] || 'FFFFFF' },
                    }),
                    new TableCell({ children: [new Paragraph(analyse.verfuegbarkeit_begruendung || '-')] }),
                ],
            }),
        ],
    });
};

export const exportSchutzbedarfsanalyseToWord = async (data) => {
    const { prozesse, analysen, anwendungen, mandantName } = data;
    const currentDate = new Date().toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const coverPage = await createCoverPage(mandantName, currentDate);

    const sections = [
        ...coverPage,
        ...createExecutiveSummary(prozesse, analysen, anwendungen || []),
        ...createProzesseSection(prozesse, analysen),
        ...createAnwendungenSection(anwendungen || [], prozesse),
    ];

    const doc = new Document({
        creator: 'Regentra',
        title: `Schutzbedarfsanalyse - ${mandantName}`,
        description: 'BSI IT-Grundschutz konforme Schutzbedarfsanalyse',
        sections: [
            {
                properties: {},
                children: sections,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `Schutzbedarfsanalyse_${mandantName}_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, fileName);
};
